import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Grid, Link } from '@material-ui/core';
import {
  downloadSignedSurgeonForm,
  getSurgeonFormById
} from '../../../../shared/src/services/SurgeonFormsService';
import NavRoutes from '../../constants/NavRoutes';
import { BASE_ROUTE } from '../../constants/BaseRoute';
import '../surgeon/SurgeonStyle.scss';
import './SurgeonFormView.scss';

const containerStyle: React.CSSProperties = {
  width: '60%',
  margin: '0 auto'
};

const containerMobileStyle: React.CSSProperties = {
  width: '100%'
};

type SurgeonMapping = {
  id: number;
  surgeon_id: number;
  is_signed: boolean;
  surgeon?: {
    id: number;
    name: string;
    surgeon_id?: string | number;
  } | null;
};

type FormDetails = {
  id: number;
  form_name: string;
  file_name?: string;
  surgeon_mappings?: SurgeonMapping[];
};

const SurgeonFormView: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormDetails | null>(null);

  const downloadSigned = async (surgeonId: number, formId: number, fallbackName: string) => {
    const response = await downloadSignedSurgeonForm(surgeonId, formId);
    const payload = response?.data || {};
    const fileName =
      payload?.meta_data?.file_name || payload?.file_name || `${fallbackName || 'signed-form'}.pdf`;
    const mimeType = payload?.mime_type || 'application/pdf';

    const raw = payload?.data ?? payload?.file_content;
    const bytes =
      raw && raw.type === 'Buffer' && Array.isArray(raw.data)
        ? new Uint8Array(raw.data)
        : raw instanceof ArrayBuffer
          ? new Uint8Array(raw)
          : Array.isArray(raw)
            ? new Uint8Array(raw)
            : null;

    if (!bytes || bytes.byteLength === 0) {
      return;
    }

    const blob = new Blob([bytes], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const response = await getSurgeonFormById(id);
        setForm(response?.data?.form || null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const mappings = form?.surgeon_mappings || [];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 960;

  return (
    <div className="surgeon-container surgeon-form-view">
      <div style={isMobile ? containerMobileStyle : containerStyle}>
        <div className="surgeon-view">
        <div className="surgeon-text">
          {t('SURGEON_FORMS.TITLE')} / {t('SURGEON_FORMS.VIEW', 'View')}
        </div>
        <div className="search-main-view" style={{ marginBottom: 0 }}>
          <Link
            role="button"
            tabIndex={0}
            className="sfv-link"
            onClick={() =>
              history.push(
                `/${BASE_ROUTE}/${NavRoutes.PRIVATE.HOME}/${NavRoutes.PRIVATE.SURGEON_FORMS}`
              )
            }
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                history.push(
                  `/${BASE_ROUTE}/${NavRoutes.PRIVATE.HOME}/${NavRoutes.PRIVATE.SURGEON_FORMS}`
                );
              }
            }}
          >
            {t('COMMON.BACK', 'Back')}
          </Link>
        </div>
      </div>

        {loading || !form ? null : (
          <div className="sfv-details">
            <div className="sfv-row">
              <span className="sfv-label">{t('SURGEON_FORMS.FORM_NAME')}:</span>
              <span className="sfv-value">{form.form_name}</span>
            </div>
            <div className="sfv-row" style={{ marginBottom: 16 }}>
              <span className="sfv-label">{t('SURGEON_FORMS.FILE_NAME', 'File Name')}:</span>
              <span className="sfv-value">{form.file_name || '-'}</span>
            </div>

            <div className="sfv-section-title">
              {t('SURGEON_FORMS.ASSIGNED_TO', 'Assigned Surgeons')}
            </div>

            <Grid container spacing={2}>
              {mappings.map(m => {
                const label =
                  m?.surgeon?.surgeon_id != null && String(m.surgeon?.surgeon_id).trim().length > 0
                    ? `${m.surgeon?.name} (${m.surgeon?.surgeon_id})`
                    : m?.surgeon?.name || `Surgeon ${m.surgeon_id}`;

                return (
                  <Grid item xs={12} sm={6} key={m.id}>
                    <div className="sfv-card">
                      <div className="sfv-surgeon-name">{label}</div>
                      {m.is_signed ? (
                        <Link
                          role="button"
                          tabIndex={0}
                          className="sfv-link"
                          onClick={() =>
                            downloadSigned(m.surgeon_id, form.id, `${form.form_name}-${m.surgeon_id}`)
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              downloadSigned(
                                m.surgeon_id,
                                form.id,
                                `${form.form_name}-${m.surgeon_id}`
                              );
                            }
                          }}
                        >
                          {t('SURGEON_FORMS.DOWNLOAD', 'Download signed form')}
                        </Link>
                      ) : (
                        <div className="sfv-muted">{t('SURGEON_FORMS.NOT_SIGNED', 'Not signed')}</div>
                      )}
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurgeonFormView;